import {
  ReactElement, useCallback, useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useToast } from '~/contexts/newToastContext';
import { getEnergyEfficiencyRating } from '~/pages/NewOverview/helpers/overview';
import { GetEnergyEfficiencyRating } from '~/pages/NewOverview/types';
import { useFilterContext } from '../../../contexts/FilterContext';
import { TitleGraph } from '../../../styles';
import {
  CardsGraphContainer,
} from '../styles';
import { rankingOptions } from './constants';
import { EnergyEfficiencyCardContent } from './Content';
import { EnergyEfficiencyCardContainer, EnergyEfficiencyCardLoading, RankingSelect } from './styles';

interface EnergyEfficiencyCardProps {
  expandedSplit: boolean;
  hasEnergy: boolean;
  order: number;
  loading: boolean,
}

export function EnergyEfficiencyCard({
  expandedSplit,
  hasEnergy,
  order,
  loading,
}: Readonly<EnergyEfficiencyCardProps>): ReactElement {
  const { client, filtersDependencies, selectedOptions } = useFilterContext();
  const [isFiltering, setIsFiltering] = useState(false);
  const [isChangingRanking, setIsChangingRanking] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const [efficiencyData, setEfficiencyData] = useState<GetEnergyEfficiencyRating | undefined>();
  const [rankingOption, setRankingOption] = useState<{name: string, value}>(rankingOptions[0]);

  function onSelectRanking(option: { name: string, value: string}): void {
    setIsChangingRanking(true);
    setRankingOption(option);
  }

  const getEfficiencyInfo = useCallback(async (): Promise<void> => {
    setIsFiltering(true);

    const rankingValue = rankingOption.value;

    const data = await getEnergyEfficiencyRating(
      Number(client),
      rankingValue,
      {
        stateIds: selectedOptions.state,
        unitIds: selectedOptions.group?.units,
        healthStatus: selectedOptions.healthStatus,
        thermalComfortStatus: selectedOptions.status,
      },
    );

    if (data) {
      setEfficiencyData(data);
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosEficienciaEnergetica'), 'error');
    }

    if (isChangingRanking) {
      setIsChangingRanking(false);
    }

    setIsFiltering(false);
  }, [isChangingRanking, filtersDependencies, rankingOption.value]);

  useEffect(() => {
    if (client && hasEnergy && !loading) {
      getEfficiencyInfo();
    }
  }, [client, hasEnergy, loading, ...filtersDependencies, rankingOption]);

  const generalLoading = useMemo(() => (loading || isFiltering) && !isChangingRanking, [loading, isFiltering, isChangingRanking]);

  if (generalLoading) {
    return (
      <EnergyEfficiencyCardLoading
        style={{ order }}
        expandedSplit={expandedSplit}
      >
        <Skeleton
          height="360px"
          borderRadius="8px"
          className="skeleton-span"
          containerClassName="skeleton-container-span"
        />
      </EnergyEfficiencyCardLoading>
    );
  }

  return (
    <EnergyEfficiencyCardContainer
      expandedSplit={expandedSplit}
      style={{ order }}
      data-testId="energy-rating-card"
    >
      <TitleGraph>{t('eficienciaEnergetica')}</TitleGraph>
      <CardsGraphContainer>
        {hasEnergy && !loading && (
          <RankingSelect
            options={rankingOptions}
            value={rankingOption}
            label={t('classificacao')}
            onSelect={onSelectRanking}
            propLabel="name"
            disabled={isFiltering}
            hideSelected
          />
        )}
        <EnergyEfficiencyCardContent
          data={isChangingRanking ? undefined : efficiencyData}
          hasEnergy={hasEnergy}
        />
      </CardsGraphContainer>
    </EnergyEfficiencyCardContainer>
  );
}
